import { GraphEdge, GraphNode, GraphPayload, GraphSourceOrigin } from "../../api";

export const SOURCE_OPTIONS: { key: GraphSourceOrigin; label: string }[] = [
    { key: "semantic", label: "Semantic" },
    { key: "context_graph", label: "Graph" },
    { key: "both", label: "Both" },
    { key: "structured", label: "Structured" },
    { key: "manual", label: "Pinned" }
];

export const SOURCE_COLORS: Record<GraphSourceOrigin, string> = {
    semantic: "#0078d4",
    context_graph: "#107c10",
    both: "#8764b8",
    structured: "#ca5010",
    manual: "#605e5c"
};

export function toggleOrigin(selectedOrigins: Set<GraphSourceOrigin>, origin: GraphSourceOrigin): Set<GraphSourceOrigin> {
    const next = new Set(selectedOrigins);
    if (next.has(origin)) {
        next.delete(origin);
    } else {
        next.add(origin);
    }
    return next;
}

export function sourceMatchesSelectedFilters(origin: GraphSourceOrigin, selectedOrigins: Set<GraphSourceOrigin>): boolean {
    if (origin === "both") {
        return selectedOrigins.has("both") || selectedOrigins.has("semantic") || selectedOrigins.has("context_graph");
    }
    if (origin === "semantic") {
        return selectedOrigins.has("semantic");
    }
    if (origin === "context_graph") {
        return selectedOrigins.has("context_graph");
    }
    return selectedOrigins.has(origin);
}

export function withSemanticCitationOrigins(payload: GraphPayload, assessmentIds: string[]): GraphPayload {
    const semanticIds = new Set(assessmentIds);
    const seenSemanticIds = new Set<string>();
    const nodes = payload.nodes.map(node => {
        const assessmentId = node.assessment_id;
        if (!assessmentId || !semanticIds.has(assessmentId)) {
            return node;
        }

        seenSemanticIds.add(assessmentId);
        const currentOrigin = node.source_origin ?? "context_graph";
        return {
            ...node,
            source_origin: currentOrigin === "context_graph" ? ("both" as GraphSourceOrigin) : currentOrigin
        };
    });

    semanticIds.forEach(assessmentId => {
        if (seenSemanticIds.has(assessmentId)) {
            return;
        }
        nodes.push({
            id: `semantic-note:${assessmentId}`,
            type: "Note",
            label: assessmentId,
            clinical_type: "note",
            source_origin: "semantic",
            assessment_id: assessmentId,
            metadata: { source: "answer citation metadata" }
        });
    });

    return {
        ...payload,
        nodes,
        metadata: {
            ...payload.metadata,
            node_count: nodes.length
        }
    };
}

export function countSourceContributions(payload: GraphPayload | null): Record<GraphSourceOrigin, number> {
    const counts: Record<GraphSourceOrigin, number> = {
        semantic: 0,
        context_graph: 0,
        both: 0,
        structured: 0,
        manual: 0
    };
    payload?.nodes.forEach(node => {
        const origin = (node.source_origin ?? "context_graph") as GraphSourceOrigin;
        if (origin === "both") {
            counts.both += 1;
            counts.semantic += 1;
            counts.context_graph += 1;
            return;
        }
        counts[origin] = (counts[origin] ?? 0) + 1;
    });
    return counts;
}

export function groupClinicalNodes(nodes: GraphNode[]) {
    const patient: GraphNode[] = [];
    const notes: GraphNode[] = [];
    const context: GraphNode[] = [];
    nodes.forEach(node => {
        const clinicalType = (node.clinical_type ?? node.type).toLowerCase();
        if (clinicalType.includes("patient")) {
            patient.push(node);
        } else if (node.assessment_id || clinicalType.includes("note")) {
            notes.push(node);
        } else {
            context.push(node);
        }
    });
    return { patient, notes, context };
}

export function clinicalLabel(node: GraphNode): string {
    const type = node.clinical_type ?? node.type;
    return type.replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
}

export function sourceOriginLabel(origin: GraphSourceOrigin | undefined): string {
    const match = SOURCE_OPTIONS.find(option => option.key === (origin ?? "context_graph"));
    return match?.label ?? "Graph";
}

export function getNodeProperty(node: GraphNode, key: string): unknown {
    const properties = node.metadata?.properties;
    if (properties && typeof properties === "object" && key in properties) {
        return (properties as Record<string, unknown>)[key];
    }
    return undefined;
}

export function safeEdgeLabel(edge: GraphEdge): string {
    return edge.label ?? edge.type.replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
}
